import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import AnimatedSpinner from 'Components/AnimatedSpinner';

import { formatDate } from 'utlis/string_format';

import ProductForm from './ProductForm';

import panelStyles from './styles.module.scss';

const TAB_OPTIONS = {
  REVIEW: 'review',
  CONTEXT: 'context',
};

const ReviewResults = ({
  reviewData,
  title,
  productDescription,
  audience,
  businessGoal,
}) => {
  const [selectedTab, setSelectedTab] = useState(TAB_OPTIONS.REVIEW);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef(null);

  const handleScroll = useCallback(() => {
    setIsScrolling(true);
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 1000);
  }, []);

  useEffect(() => () => clearTimeout(scrollTimerRef.current), []);

  const isContextTab = selectedTab === TAB_OPTIONS.CONTEXT;

  return (
    <div className={panelStyles.fullWidthStageCard}>
      <div className={panelStyles.processingTabs}>
        <button
          type="button"
          className={cn(panelStyles.processingTab, {
            [panelStyles.processingTabActive]: selectedTab === TAB_OPTIONS.REVIEW,
          })}
          onClick={() => setSelectedTab(TAB_OPTIONS.REVIEW)}
        >
          Audit results
        </button>
        <button
          type="button"
          className={cn(panelStyles.processingTab, {
            [panelStyles.processingTabActive]: selectedTab === TAB_OPTIONS.CONTEXT,
          })}
          onClick={() => setSelectedTab(TAB_OPTIONS.CONTEXT)}
        >
          Context
        </button>
      </div>

      <div
        className={cn(
          'flex-1 min-h-0 w-full',
          isContextTab
            ? cn(
              'overflow-y-auto pb-24',
              panelStyles['scrollable-content'],
              { [panelStyles['is-scrolling']]: isScrolling }
            )
            : 'overflow-hidden flex flex-col'
        )}
        onScroll={isContextTab ? handleScroll : undefined}
      >
        {isContextTab
          ? (
            <div className="w-full">
              <ProductForm
                title={title}
                productDescription={productDescription}
                audience={audience}
                businessGoal={businessGoal}
                reviewData={reviewData}
                mode="display"
              />
            </div>
          )
          : (
            <div className="flex flex-col justify-center items-center gap-10 w-full h-full">
              <AnimatedSpinner />
              <div className="flex flex-col items-center gap-4 mt-32">
                <h2 className="sm:text-xl 2xl:text-2xl font-semibold text-gray-900 text-center">
                  Your audit is being processed
                </h2>
                <p className="sm:text-md 2xl:text-lg text-gray-900 text-center">
                  This might take a few minutes
                </p>
              </div>
            </div>
          )}
      </div>

      <div className={cn(panelStyles['review-footer'], 'flex items-center justify-between')}>
        <div className="flex flex-col gap-8 text-xs">
          <span className="text-gray-500 h-4">
            Last updated
          </span>
          <span className="text-gray-900 h-4">
            {reviewData?.updated_at ? formatDate(reviewData?.updated_at) : 'Just now'}
          </span>
        </div>
      </div>
    </div>
  );
};

ReviewResults.propTypes = {
  reviewData: PropTypes.object,
  title: PropTypes.string.isRequired,
  productDescription: PropTypes.string,
  audience: PropTypes.string,
  businessGoal: PropTypes.string,
};

ReviewResults.defaultProps = {
  productDescription: '',
  audience: '',
  businessGoal: '',
};

export default ReviewResults;
